import React from 'react'
import { css } from '@emotion/css'
import { LegacyGrid, SortConfig } from '../../legacy-design-system/legacy-grid'
import { SensitiveDataPolicySummary } from '.'
import { GroupIDCell, KeywordsCell, ToggleCell } from './cell-content'
import { TextInput } from '../../legacy-design-system/form-text'
import { hasRole, Role } from '../roles'
import { sendDefaultEvent } from '../analytics'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import {
  getRbacStatus,
  userHasResourceAction
} from 'contrastBase/accessControl'

export interface SensitiveDataGridViewProps {
  readonly loading: boolean
  readonly organizationID: string
  readonly organizationRole: Role
  readonly rowData: SensitiveDataPolicySummary[]
  readonly onSort: (sortConfig: SortConfig<SensitiveDataPolicySummary>) => void
  readonly onSearch: (value: string) => void
  readonly activeSortConfigs: Array<SortConfig<SensitiveDataPolicySummary>>
  readonly refetch: () => Promise<any>
}

export function SensitiveDataGridView({
  organizationID,
  organizationRole,
  rowData,
  onSearch,
  onSort,
  loading,
  activeSortConfigs,
  refetch
}: SensitiveDataGridViewProps) {
  const { isAssessEnforced } = getRbacStatus()
  const isEditable = isAssessEnforced
    ? userHasResourceAction(
        'SENSITIVE_DATA',
        'PROTECT_SENSITIVE_DATA_POLICY_MANAGE'
      )
    : hasRole(organizationRole, 'RULES_ADMIN')

  return (
    <React.Fragment>
      <div
        className={css`
          display: flex;
          padding: 10px 11px 9px 13px;
          align-items: center;
        `}
      >
        <span
          className={css`
            flex: 0.5;
            padding-right: 30px;
          `}
        >
          <ContrastFormattedMessage id="placeholder_sensitiveData_findSensitiveDataType">
            {(placeholderMessage) => (
              <TextInput
                label=""
                disabled={loading}
                renderLabel={() => false}
                placeholder={placeholderMessage}
                onChange={(event) => {
                  onSearch(event)
                  sendDefaultEvent('sensitive.data.grid.search')
                }}
              />
            )}
          </ContrastFormattedMessage>
        </span>
      </div>
      <LegacyGrid<SensitiveDataPolicySummary>
        data-testid="sensitive-data-manager-grid"
        rowClassName={css`
          vertical-align: top;
        `}
        onSort={onSort}
        loading={loading}
        rowData={rowData}
        emptyGridMessageConfig={{
          id: 'detail_sensitiveData_noneFound'
        }}
        columnConfigs={[
          {
            styles: {
              width: '300px'
            },
            heading: {
              id: 'title_sensitiveData_dataType'
            },
            sorting: {
              field: 'name'
            },
            CellContent: ({ rowData: { id, name, groupID } }) => (
              <GroupIDCell
                organizationID={organizationID}
                isEditable={isEditable}
                rowData={{ id, name, groupID }}
              />
            )
          },
          {
            styles: {
              maxWidth: '300px'
            },
            heading: {
              id: 'title_sensitiveData_keywords'
            },
            CellContent: ({ rowData: { keywords } }) => (
              <KeywordsCell rowData={{ keywords }} />
            )
          },
          {
            styles: {
              width: '150px'
            },
            horizontalCenter: true,
            heading: {
              id: 'title_sensitiveData_enabledGroup'
            },
            CellContent: ({ rowData: { id, enabled, required } }) => (
              <ToggleCell
                organizationID={organizationID}
                rowData={{
                  id,
                  enabled,
                  disabled: !isEditable,
                  required
                }}
                refetch={refetch}
              />
            )
          }
        ]}
        activeSortConfigs={activeSortConfigs}
      />
    </React.Fragment>
  )
}
